import { supabase } from "../../config/supabaseClient";
import axios from "axios";
import { buildCreatorCollectionUrl } from "../url/getCreatorPublicBaseUrl";

// --- Small utilities (no behavior change) ---
const safeParseArray = (value: unknown): any[] | null => {
  if (typeof value !== "string") return Array.isArray(value) ? (value as any[]) : null;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/** Extract email address from subscriber data */
const extractEmailFromSubscriber = (subscriber: SubscriberData): string => {
  const detailsArray = safeParseArray(subscriber.other_details);
  if (detailsArray) {
    const emailObj = detailsArray.find((obj) => obj?.Email);
    if (emailObj?.Email) return emailObj.Email as string;
  }
  return subscriber.details;
};

// 👉 Place sanitize helper here
const CAROUSEL_HTML_MARKER = "SearchBubble Carousel - Email Static Snapshot";
const containsCarouselEmbed = (html?: string) => {
  if (typeof html !== "string") return false;
  const normalized = html.toLowerCase();
  return (
    html.includes(CAROUSEL_HTML_MARKER) ||
    html.includes('data-sb-email-embed="true"') ||
    html.includes('data-sb-email-embed-placeholder="true"') ||
    (normalized.includes("scroll to see more products") &&
      normalized.includes('role="presentation"')) ||
    (normalized.includes("<iframe") && normalized.includes("/component/"))
  );
};

function isDashboardPlatformHost(hostname: string): boolean {
  const h = String(hostname || "").toLowerCase();
  return (
    h.includes("searchbubble.com") ||
    h === "localhost" ||
    h === "127.0.0.1" ||
    h.endsWith(".localhost") ||
    h.endsWith(".vercel.app")
  );
}

function isLegacyBrowsableApexCollectionUrl(
  hostname: string,
  pathname: string,
  creatorUsername: string
): boolean {
  const h = String(hostname || "").toLowerCase();
  const isApex =
    h === "browsable.ai" ||
    h === "www.browsable.ai" ||
    h === "browsable.bio" ||
    h === "www.browsable.bio";
  if (!isApex) return false;

  const path = String(pathname || "").replace(/^\/+/, "");
  return path === creatorUsername || path.startsWith(`${creatorUsername}/`);
}

/** Rewrite carousel links that still point at the dashboard / legacy SearchBubble host. */
const rewriteStaleCarouselLinks = (
  html: string,
  creatorUsername?: string
): string => {
  if (!html || !creatorUsername || typeof document === "undefined") return html;
  if (!containsCarouselEmbed(html)) return html;

  const rewriteHref = (href: string, anchor?: HTMLAnchorElement): string => {
    const trimmed = String(href || "").trim();
    if (!trimmed || trimmed.startsWith("#") || trimmed.startsWith("mailto:")) {
      return href;
    }

    const anchorText = (anchor?.textContent || "").toLowerCase();
    const isViewAllLink =
      anchorText.includes("view all") || anchorText.includes("more items");

    const extractRoute = (path: string): string => {
      const clean = String(path || "").replace(/^\/+/, "").replace(/\/+$/, "");
      const parts = clean.split("/").filter(Boolean);
      if (!parts.length) return "";

      if (parts[0] === creatorUsername) {
        return parts.slice(1).join("/");
      }
      if (parts.length >= 2) {
        return parts.slice(1).join("/");
      }
      return parts[0];
    };

    try {
      if (/^https?:\/\//i.test(trimmed)) {
        const u = new URL(trimmed);
        const shouldRewrite =
          isDashboardPlatformHost(u.hostname) ||
          (isViewAllLink &&
            isLegacyBrowsableApexCollectionUrl(
              u.hostname,
              u.pathname,
              creatorUsername
            ));
        if (!shouldRewrite) return href;

        const route = extractRoute(u.pathname);
        const next = buildCreatorCollectionUrl({ username: creatorUsername, route });
        return next || href;
      }

      if (!isViewAllLink) return href;

      const route = extractRoute(trimmed);
      const next = buildCreatorCollectionUrl({ username: creatorUsername, route });
      return next || href;
    } catch {
      return href;
    }
  };

  try {
    const wrapper = document.createElement("div");
    wrapper.innerHTML = html;
    wrapper.querySelectorAll("a[href]").forEach((node) => {
      const anchor = node as HTMLAnchorElement;
      const current = anchor.getAttribute("href");
      if (!current) return;
      const next = rewriteHref(current, anchor);
      if (next && next !== current) anchor.setAttribute("href", next);
    });
    return wrapper.innerHTML;
  } catch {
    return html;
  }
};

// --- Email HTML sanitizer (add this near the top, with other helpers) ---
const sanitizeHtmlForEmail = (html: string): string => {
  if (!html) return "";
  try {
    if (typeof document === "undefined") return html;
    const wrapper = document.createElement("div");
    wrapper.innerHTML = html;

    const htmlNode = wrapper.querySelector("html") as HTMLElement | null;
    const root = (htmlNode || wrapper) as HTMLElement;
    const bodyNode = root.querySelector("body") as HTMLElement | null;
    const contentRoot = (bodyNode || root) as HTMLElement;

    // Remove dangerous/unsupported tags for email clients
    contentRoot.querySelectorAll("script, style, meta, link, head").forEach((el) => el.remove());

    // Replace iframe-based embeds (most email clients strip them) with a link fallback.
    contentRoot.querySelectorAll("iframe").forEach((iframe) => {
      const el = iframe as HTMLIFrameElement;
      const src = el.getAttribute("src") || "";
      if (src) {
        const a = document.createElement("a");
        a.setAttribute("href", src);
        a.textContent = "View embedded content";
        a.style.display = "inline-block";
        a.style.padding = "10px 16px";
        a.style.border = "1px solid #e5e7eb";
        a.style.borderRadius = "8px";
        a.style.textDecoration = "none";
        a.style.color = "#111111";
        iframe.replaceWith(a);
      } else {
        iframe.remove();
      }
    });

    return contentRoot.innerHTML;
  } catch {
    // Best-effort fallback: at least remove scripts
    return (html || "").replace(/<script[\s\S]*?<\/script>/gi, "");
  }
};

// Minimal, email-safe CSS to better match site styles (esp. Twitter blocks)
const wrapEmailWithStyles = (bodyHtml: string): string => {
  const styles = `
    <style type="text/css">
      /* Layout */
      .sb-container{max-width:100%;padding:16px;background:#ffffff;color:#111111;font-family:Arial,Helvetica,sans-serif;line-height:1.5}
      .sb-section{margin:16px 0}
      /* Headings & paragraphs */
      h1,h2,h3,h4,h5,h6{margin:0}
      p{margin:0;line-height:1.5}
      .ql-align-center{text-align:center !important}
      .ql-align-right{text-align:right !important}
      .ql-align-justify{text-align:justify !important}
      .ql-indent-1{margin-left:1.5em !important}
      .ql-indent-2{margin-left:3em !important}
      /* Buttons */
      .sb-btn{display:inline-block;padding:10px 16px;background:#111111;color:#ffffff !important;text-decoration:none;border-radius:6px;font-weight:600}
      .sb-btn:hover{opacity:.9}
      /* Email carousel helpers */
      .sb-email-carousel{box-sizing:border-box}
      .sb-email-carousel-viewport{overflow:hidden}
      .sb-email-carousel-strip{white-space:nowrap}
      .sb-email-carousel-strip::-webkit-scrollbar{display:none}
      .sb-email-card{box-sizing:border-box}
      .sb-email-card img,.sb-email-card table{max-width:100%}
      .sb-email-card-cell{padding:0 12px 0 0}
      /* Twitter/X styles similar to site */
      .twitter-post-img-wrp{width:100%;background:#000000;border-radius:8px;overflow:hidden;position:relative}
      .twitter-post-img{width:100%;display:block;opacity:.95}
      .twitter-post-img-date{position:absolute;right:10px;top:10px;background:rgba(0,0,0,.55);color:#ffffff;padding:6px 10px;border-radius:6px;font-size:12px}
      .twitter-label{display:inline-block;color:#ffffff;background:#111111;border:1px solid #2b2b2b;padding:4px 8px;border-radius:6px;font-size:12px}
      .bi-twitter-x,.twitter-icon{vertical-align:middle}
      /* Link treatment */
      a{color:#111111}
      a:hover{opacity:.85}
      /* Mobile */
      @media only screen and (max-width:520px){
        .sb-container{padding:12px}
        .sb-email-carousel-strip{white-space:normal !important}
        .sb-email-carousel-table{width:100% !important}
        .sb-email-card-cell{display:block !important;width:100% !important;padding:0 0 16px 0 !important}
        .sb-email-card{display:block !important;width:100% !important;margin-right:0 !important}
        .sb-email-card img,.sb-email-card table{width:100% !important;height:auto !important}
      }
    </style>
  `;
  return `<!DOCTYPE html><html><head><meta http-equiv="Content-Type" content="text/html; charset=UTF-8"/>${styles}</head><body><div class="sb-container">${bodyHtml}</div></body></html>`;
};


// Constants
const EMAIL_API_URL = 'https://dev.thesearchbubble.com/send-subscriber-email';
const REQUEST_TIMEOUT = 10000; // 10 seconds

// Interfaces
interface EmailTemplate {
  email_subject: string;
  email_body: string;
  createdAt?: string;
}

interface SubscriberData {
  id: string;
  user_id: string;
  campaign_id: string;
  campaign_name: string;
  details: string;
  other_details: string;
  created_at: string;
}

interface CampaignData {
  id: string;
  user_id: string;
  campaign_name: string;
  campaign_button_text: string;
  is_active: boolean;
  metadata: {
    form_title: string;
    fields: any[];
  };
  email_subject?: string;
  email_body?: string;
  welcome_email_subject?: string;
  welcome_email_body?: string;
  bulk_email_subject?: string;
  bulk_email_body?: string;
  image?: string;
}

interface EmailServiceResponse {
  success: boolean;
  message: string;
  error?: boolean;
}

// Helper: get creator display name by user_id
const getCreatorDisplayNameByUserId = async (userId?: string): Promise<string | null> => {
  if (!userId) return null;
  try {
    const { data, error } = await supabase
      .from('user')
      .select('name')
      .eq('user_id', userId)
      .single();
    if (!error && data?.name) return data.name as string;
  } catch {}
  return null;
};

// Helper: get creator email by user_id
const getCreatorEmailByUserId = async (userId?: string): Promise<string | null> => {
  if (!userId) return null;
  try {
    const { data, error } = await supabase
      .from("user")
      .select("email")
      .eq("user_id", userId)
      .single();
    if (!error && data?.email) return data.email as string;
  } catch {}
  return null;
};

/**
 * Send welcome email to a new subscriber
 * This function is called when a new subscriber connects to a campaign
 */
export const sendWelcomeEmailToSubscriber = async (
  subscriber: SubscriberData,
  campaign: CampaignData,
  username?: string,
  userEmail?: string,
  creatorName?: string,
  creatorEmail?: string,
  creatorUsername?: string 
): Promise<EmailServiceResponse> => {
  const emailAddress = extractEmailFromSubscriber(subscriber);
  
  try {
    // Prefer the explicit email_subject/email_body passed by caller (bulk + test paths),
    // otherwise fall back to stored welcome template, then legacy fields.
    const subjectToUse =
      campaign.email_subject ||
      campaign.welcome_email_subject ||
      campaign.bulk_email_subject ||
      "";
    const bodyToUse =
      campaign.email_body ||
      campaign.welcome_email_body ||
      campaign.bulk_email_body ||
      "";

    if (!subjectToUse || !bodyToUse) {
      return { success: false, message: "No email template found for this campaign" };
    }

    // Resolve creator display name consistently
    const fetchedName = await getCreatorDisplayNameByUserId(campaign.user_id);
    const displayName = creatorName || fetchedName || username || 'Creator';
    const fetchedEmail = await getCreatorEmailByUserId(campaign.user_id);
    const resolvedCreatorEmail = creatorEmail || userEmail || fetchedEmail || undefined;

    const processedSubject = replaceEmailVariables(
      subjectToUse,
      subscriber,
      campaign.campaign_name,
      displayName
    );

    const processedBody = replaceEmailVariables(
      bodyToUse,
      subscriber,
      campaign.campaign_name,
      displayName
    );

    const carouselSafeBody = rewriteStaleCarouselLinks(
      processedBody,
      creatorUsername || username
    );

    const bodyForEmail = containsCarouselEmbed(carouselSafeBody)
      ? carouselSafeBody
      : sanitizeHtmlForEmail(carouselSafeBody);

    const finalHtml = wrapEmailWithStyles(bodyForEmail);



    const textBody = carouselSafeBody.replace(/<[^>]*>/g, '').replace(/\n\s*\n/g, '\n').trim();
    const response = await axios.post(
      EMAIL_API_URL,
      {
        user: displayName,
        user_id: campaign.user_id,
        campaign_id: campaign.id,
        subscriber_id: subscriber.id,
        campaign_name: campaign.campaign_name,
        to: emailAddress,
        subject: processedSubject,
        text: textBody,
        html: finalHtml,
        subscriber_data: subscriber.other_details,
        creator_name: displayName,
        creator_email: resolvedCreatorEmail,
        creator_username: creatorUsername,
        reply_to: resolvedCreatorEmail,
        from_name: displayName,
        from_email: resolvedCreatorEmail,
      },
      { headers: { "Content-Type": "application/json" }, timeout: REQUEST_TIMEOUT }
    );

    if (response?.data?.success) {
      return { success: true, message: "Welcome email sent successfully" };
    }
    throw new Error(response?.data?.message || "Failed to send email");
  } catch (error: any) {
    return { success: false, message: error.response?.data?.message || error.message || "Failed to send email", error: true };
  }
};

/**
 * Replace variables in email content with actual subscriber data
 */
const replaceEmailVariables = (
  content: string,
  subscriberData: SubscriberData,
  campaignName: string,
  username?: string
): string => {
  if (!content) return content;

  const emailAddress = extractEmailFromSubscriber(subscriberData);
  const creatorName = username || 'Creator';

  const replaceAll = (input: string, token: string, value: string) =>
    input.replace(new RegExp(`\\{\\{${token}\\}\\}`, "gi"), value);

  let processedContent = content;

  // Basic aliases (case-insensitive)
  processedContent = replaceAll(processedContent, "campaign_name", campaignName);
  processedContent = replaceAll(processedContent, "creator_name", creatorName);
  processedContent = replaceAll(processedContent, "username", username || creatorName);
  processedContent = replaceAll(processedContent, "email", emailAddress);
  processedContent = replaceAll(processedContent, "subscriber_email", emailAddress);
  processedContent = replaceAll(processedContent, "details", subscriberData.details || "");
  processedContent = replaceAll(processedContent, "subscriber_details", subscriberData.details || "");

  // Replace subscriber field variables from other_details
  const details = safeParseArray(subscriberData.other_details);
  if (details) {
    details.forEach((obj: any) => {
      if (obj && typeof obj === "object") {
        Object.entries(obj).forEach(([key, value]) => {
          const valueStr = String(value);
          const normalized = key.toLowerCase().trim().replace(/\s+/g, "_");

          // Replace {{key}} and {{key_normalized}}
          processedContent = replaceAll(processedContent, key, valueStr);
          processedContent = replaceAll(processedContent, normalized, valueStr);

          // Friendly aliases (so old templates keep working)
          if (normalized === "full_name" || normalized === "fullname" || normalized === "name") {
            processedContent = replaceAll(processedContent, "name", valueStr);
          }
          if (normalized === "email") {
            processedContent = replaceAll(processedContent, "email", valueStr);
            processedContent = replaceAll(processedContent, "subscriber_email", valueStr);
          }
          if (
            normalized === "phone" ||
            normalized === "phone_number" ||
            normalized === "mobile" ||
            normalized === "mobile_number" ||
            normalized === "contact" ||
            normalized === "contact_number"
          ) {
            processedContent = replaceAll(processedContent, "contact", valueStr);
            processedContent = replaceAll(processedContent, "phone", valueStr);
          }
        });
      }
    });
  }

  return processedContent;
};

/**
 * Get email template for a campaign
 */
export const getCampaignEmailTemplate = async (
  campaignId: string
): Promise<EmailTemplate | null> => {
  try {
    const { data, error } = await supabase
      .from("creators_campaigns")
      .select("email_subject, email_body")
      .eq("id", campaignId)
      .single();

    if (error || !data?.email_subject || !data?.email_body) {
      return null;
    }

    return {
      email_subject: data.email_subject,
      email_body: data.email_body,
    };
  } catch {
    return null;
  }
};

/**
 * Check if a campaign has an email template
 */
export const hasEmailTemplate = async (campaignId: string): Promise<boolean> => {
  const template = await getCampaignEmailTemplate(campaignId);
  return template !== null;
};

/**
 * Send bulk emails to multiple subscribers
 */
export const sendBulkEmailsToSubscribers = async (
  subscribers: SubscriberData[],
  campaign: CampaignData,
  username?: string
): Promise<EmailServiceResponse> => {
  if (!campaign.email_subject || !campaign.email_body) {
    return {
      success: false,
      message: "No email template found for this campaign",
    };
  }

  try {
    const results = await Promise.allSettled(
      subscribers.map((subscriber) =>
        sendWelcomeEmailToSubscriber(subscriber, campaign, username)
      )
    );

    const successful = results.filter(
      (result) => result.status === "fulfilled" && result.value.success
    ).length;

    const failed = results.length - successful;

    return {
      success: successful > 0,
      message: `Sent ${successful} emails successfully. ${failed} failed.`,
    };
  } catch (error: any) {
    return {
      success: false,
      message: error.message || "Failed to send bulk emails",
      error: true,
    };
  }
};

/**
 * Test email sending functionality
 */
export const testEmailSending = async (
  campaignId: string,
  testEmail: string,
  username?: string
): Promise<EmailServiceResponse> => {
  try {
    const campaign = await getCampaignById(campaignId);
    if (!campaign) {
      return {
        success: false,
        message: "Campaign not found",
      };
    }

    const testSubscriber: SubscriberData = {
      id: "test-subscriber",
      user_id: campaign.user_id,
      campaign_id: campaignId,
      campaign_name: campaign.campaign_name,
      details: testEmail,
      other_details: JSON.stringify([
        { "Full Name": "Test User" },
        { Email: testEmail },
      ]),
      created_at: new Date().toISOString(),
    };

    return await sendWelcomeEmailToSubscriber(testSubscriber, campaign, username);
  } catch (error: any) {
    return {
      success: false,
      message: error.message || "Test email failed",
      error: true,
    };
  }
};

/**
 * Get campaign by ID
 */
const getCampaignById = async (campaignId: string): Promise<CampaignData | null> => {
  try {
    const { data, error } = await supabase
      .from("creators_campaigns")
      .select("*")
      .eq("id", campaignId)
      .single();

    if (error) {
      return null;
    }

    return data as CampaignData;
  } catch {
    return null;
  }
}; 